import { postJSON } from 'common/services/api'
import { showToast } from 'common/utils'
import { useCallback } from 'react'
import { useMutation, useQueryClient } from 'react-query'

type UseRiskAssessmentRestoreProps = {
  riskAssessmentId: string | undefined
  residentId?: string
  onRestoreSuccess?: () => void
}

const useRiskAssessmentRestore = ({
  riskAssessmentId,
  residentId,
  onRestoreSuccess
}: UseRiskAssessmentRestoreProps) => {
  const queryClient = useQueryClient()

  const handleError = (err: unknown) => {
    showToast({
      title: err as string,
      type: 'error'
    })
  }

  const restoreFunction = () =>
    postJSON({
      url: `list/risk-assessments/${riskAssessmentId}/restore`,
      method: 'POST'
    })

  const updateRiskAssessmentQueries = useCallback(() => {
    queryClient.invalidateQueries(['risk-assessment', riskAssessmentId])
    queryClient.invalidateQueries(['risk-assessments'])
    queryClient.invalidateQueries(['resident', residentId, 'risk-assessments'])
    queryClient.invalidateQueries(['list-risk-assessments'])
  }, [queryClient, riskAssessmentId, residentId])

  const { mutateAsync: restoreMutation, isLoading } = useMutation<
    any,
    unknown,
    void
  >(['risk-assessment', riskAssessmentId, 'restore'], restoreFunction, {
    onSuccess: () => {
      showToast({
        title: 'Risk assessment restored successfully',
        type: 'success'
      })
      onRestoreSuccess?.()
      updateRiskAssessmentQueries()
    },
    onError: (err) => handleError(err)
  })

  return {
    restoreRiskAssessment: restoreMutation,
    isLoading
  }
}

export default useRiskAssessmentRestore
